import Web3 from 'web3';
import Decimal from 'decimal.js';
import React, {
  useState, useEffect, useRef, useCallback, useMemo
} from 'react';
import { useTranslation } from "react-i18next";
import {
  Layout, message, Spin, Button, Input, Row, Col, Modal
} from "antd";
import _ from 'lodash';

import { useSelector } from 'react-redux';
import styled from 'styled-components'
import * as Service from '../core/Service'

import ERC20ABI from '../data/ERC20.ABI.json';
import AIRDROPABI from '../data/AirdropERC20.ABI.json';

const { Header } = Layout;

const AirDrop = () => {
  const [t] = useTranslation();

  const [loading, setLoading] = useState(false);
  const [walletList, setWalletList] = useState([])
  const [amount, setAmount] = useState(0);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [ERC20Address, setERC20Address] = useState(null);
  const [currentChain, setCurrentChain] = useState()

  const web3 = useRef();

  const {
    IS_TESTNET,
    AIRDROP_CONTRACT_ADDRESS,
  } = useSelector((state) => state.app.config);
  console.log('IS_TESTNET :>> ', IS_TESTNET);
  console.log('AIRDROP_CONTRACT_ADDRESS :>> ', AIRDROP_CONTRACT_ADDRESS);

  const CustomText = styled.p`
    font-family: Open Sans;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0em;
    text-align: center;
    margin-left: 5px;
    margin-right: 5px;
    color: #fff
  `

  const requiredChainId = useMemo(() => (IS_TESTNET ? Web3.utils.toHex("11155111") : Web3.utils.toHex("137")), [IS_TESTNET])

  // eth_accounts always returns an array.
  const handleAccountsChanged = useCallback((accounts) => {
    const [account] = accounts;
    setCurrentAccount(account);
    if (!account) {
      // MetaMask is locked or the user has not connected any accounts.
      handlerError({ message: 'Please connect to MetaMask.' });
    }
  }, [])

  useEffect(() => {
    if (!window?.ethereum) { return }
    window.ethereum.on('accountsChanged', handleAccountsChanged);
    return () => {
      if (!window?.ethereum) { return }
      window.ethereum?.removeListener('accountsChanged', handleAccountsChanged);
    }
  }, [handleAccountsChanged])

  // Handle chain switched to undesired chain
  const onChainChanged = useCallback(async (chain) => {
    if (!window?.ethereum) { return }
    setCurrentChain(chain)
    if (chain === requiredChainId) { return }
    await window.ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [
        {
          chainId: requiredChainId
        }
      ]
    });
  }, [requiredChainId])

  useEffect(() => {
    if (!window?.ethereum) { return }
    window.ethereum?.on('chainChanged', onChainChanged);
    return () => {
      window.ethereum?.removeListener('chainChanged', onChainChanged);
    }
  }, [onChainChanged])

  const checkWalletIsConnected = useCallback(async () => {
    const { ethereum } = window;
    const [[account], chainId] = await Promise.all([
      await ethereum.request({ method: 'eth_requestAccounts' }),
      await ethereum.request({ method: "eth_chainId", params: [] })
    ])
    setCurrentAccount(account)
    setCurrentChain(chainId)

    // switch chain
    try {
      await ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: requiredChainId}],
      });
    } catch (error) {
      // This error code indicates that the chain has not been added to MetaMask.
      if (error.code === 4902) {
        try {
          if (IS_TESTNET) {
            return await ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: Web3.utils.toHex("11155111"),
                  chainName: 'Sepolia',
                  rpcUrls: ['https://eth-sepolia.g.alchemy.com/v2/demo']
                },
              ],
            });
          }

          return await ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: Web3.utils.toHex("137"),
                chainName: 'Polygon Mainnet',
                rpcUrls: ['https://polygon-rpc.com']
              },
            ],
          });
        } catch (addError) {
          return handlerError({ message: addError.message })
        }
      }
      return handlerError({ message: error.message })
    }
  }, [IS_TESTNET, requiredChainId])

  const initWeb3 = useCallback(async () => {
    setLoading(true);
    const { ethereum } = window;
    if (!ethereum) {
      return message.error(t('please_install_metamask'));
    }
    web3.current = new Web3(ethereum);
    web3.current.eth.handleRevert = true;

    try {
      await checkWalletIsConnected();
    } catch (error) {
      console.log({err: error});
      handlerError({message: error?.message})
    } finally {
      setLoading(false);
    }
  }, [checkWalletIsConnected, t])

  useEffect(() => {
    initWeb3();
  }, [initWeb3])

  const handlerError = ({
    message = ""
  }) => {
    setLoading(false);
    Modal.error({
      title: (<CustomText style={{ color: "#000" }}>{message}</CustomText>),
      onOk() {
      },
      centered: true,
      okText: 'OK'
    });
  }

  const getRevertReason = async (txHash) => {
    const tx = await web3.current.eth.getTransaction(txHash)
    let result = await new Promise((resolve, reject) => {
      web3.current.eth.call(tx, tx.blockNumber).catch((err) => {
        // console.log("RevertReason >>> :", err.message)
        resolve(err.message)
      })
    })
    return result;
  }

  const onFinish = async () => {
    setLoading(true)

    const trimmedWalletList = _.filter(walletList, (obj) => {
      return !_.isEmpty(obj);
    });

    try {
      if (_.isEmpty(trimmedWalletList)) return handlerError({ message: 'Please Input At Least One Wallet Address for Airdrop' })
      if (amount === 0) return handlerError({ message: 'Please Input Amount for Airdrop' })
      if (_.isNil(currentAccount)) return handlerError({message: 'Please connect to metamask'})
      if (currentChain !== requiredChainId) return handlerError({message: `Please switch to ${IS_TESTNET ? 'Mumbai Testnet' : 'Polygon Mainnet'}`})
      // const {result: AirDropContractInfo} = await Service.call('get', '/api/admin/airdrop/contract')
      // const {contract_address: AIRDROP_CONTRACT_ADDRESS} = AirDropContractInfo;

      const AirdropContract = await new web3.current.eth.Contract(AIRDROPABI, AIRDROP_CONTRACT_ADDRESS);
      const ERC20Contract = await new web3.current.eth.Contract(ERC20ABI, ERC20Address);

      const userBalance = await ERC20Contract.methods.balanceOf(currentAccount).call();
      const decimals = await ERC20Contract.methods.decimals().call();
      const toWeiAmount = new Decimal(amount).times(new Decimal(10 ** decimals));
      const totalFromWeiAmount = toWeiAmount.times(_.size(trimmedWalletList));

      if (totalFromWeiAmount.gt(new Decimal(userBalance))) return handlerError({ message: 'No Enough ERC20 for Airdrop' });

      const amountList = _.map(trimmedWalletList, () => toWeiAmount.toString())

      await ERC20Contract.methods.approve(AIRDROP_CONTRACT_ADDRESS, totalFromWeiAmount.toString()).send({from: currentAccount})

      const userAllowance = await ERC20Contract.methods.allowance(currentAccount, AIRDROP_CONTRACT_ADDRESS).call();

      if (totalFromWeiAmount.gt(new Decimal(userAllowance))) return handlerError({ message: 'No Enough ERC20 Allowance for Airdrop' });

      let txHash = "";
      txHash = await new Promise((resolve, reject) => {
        AirdropContract.methods.airdropERC20(ERC20Address, trimmedWalletList, amountList)
          .send({ from: currentAccount })
          .once("transactionHash", async (hash) => {
            resolve(hash);
          })
          .on("error", async (err, receipt) => {
            // console.log("publicMintERC20 mint error:", err)
            const {
              transactionHash
            } = receipt
            const result = await getRevertReason(transactionHash)
            if (result) handlerError({ message: result, redirect: false });
          })
      })
      Modal.success({title: `Transaction Hash ${txHash}`, okText: 'OK', centered: true,})
      setWalletList([]);
      setAmount(0)
      setLoading(false);
    } catch (error) {
      console.log(error)
      if (error.code) handlerError({ message: error.message });
    }
  }

  return (
    <Spin spinning={loading}>
      123
    </Spin>
  );
};

export default AirDrop;
